import React, {
    createContext,
    useContext,
    useRef,
    useEffect,
    useState,
    useMemo,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import io from 'socket.io-client'

import { AUTH_FORGOT_PASSWORD_URL, AUTH_LOGIN_URL } from '../components/app/routes'

import { getSession } from '../store/auth/authActions'
import { inAppReceived } from '../store/notification/notificationSlice'
import { fetchInAppUnreadCount } from '../store/notification/notificationActions'

export const SocketContext = createContext()

const IN_APP_NOTIFICATION = 'in_app_notification'

const SOCKET_DEBUG = false

const SOCKET_LABEL =
    process.env.REACT_APP_SOCKET_DEBUG_LABEL || 'admin'

function socketLog(...parts) {
    if (!SOCKET_DEBUG) return
    console.log(`[socket:${SOCKET_LABEL}]`, ...parts)
}

function tokenHint(t) {
    if (!t || typeof t !== 'string') return '(none)'
    if (t.length <= 16) return `(len=${t.length})`
    return `${t.slice(0, 6)}…${t.slice(-4)} (len=${t.length})`
}

function resolveSocketBaseUrl() {
    const trim = (u) => String(u || '').trim().replace(/\/+$/, '')
    const stripApi = (u) => trim(u).replace(/\/api\/v1\/?$/i, '')
    return (
        stripApi(process.env.REACT_APP_SOCKET_DOMAIN || '') ||
        trim(process.env.REACT_APP_SOCKET_URL || '') ||
        trim(process.env.REACT_APP_BACKEND_URL || '') ||
        (process.env.REACT_APP_BACKEND_DOMAIN
            ? stripApi(process.env.REACT_APP_BACKEND_DOMAIN)
            : '')
    )
}

const AUTH_PAGE_PATHS = [AUTH_LOGIN_URL, AUTH_FORGOT_PASSWORD_URL]

const ConnectedSocketProvider = ({ children }) => {
    const [socketInstance, setSocketInstance] = useState(null)
    const dispatch = useDispatch()
    const refreshAfterErrorRef = useRef(false)

    const socketUrl = useMemo(() => resolveSocketBaseUrl(), [])

    const profileId = useSelector((state) => state.profile.currentProfile?._id)
    const accessToken = useSelector((state) => state.auth?.accessToken ?? '')

    const socket = useRef(null)

    const handleOnMessage = () => {}

    const handleInAppNotification = (payload) => {
        const n = payload?.notification
        if (n?.id) {
            dispatch(inAppReceived(n))
        }
    }

    useEffect(() => {
        const token =
            (typeof accessToken === 'string' && accessToken.length > 0
                ? accessToken
                : null) || localStorage.getItem('auth_token')

        socketLog('effect run', {
            url: socketUrl || '(empty)',
            profileIdOptional: profileId ? String(profileId) : '(none)',
            client: 'admin',
            token: tokenHint(token),
            hasLocalStorageAuthToken: Boolean(localStorage.getItem('auth_token')),
        })

        if (!socketUrl || !token) {
            socketLog('skip io()', {
                reason: !socketUrl ? 'missing_socket_url' : 'missing_token',
            })
            if (socket.current) {
                socket.current.disconnect()
                socket.current = null
            }
            setSocketInstance(null)
            return
        }

        if (socket.current) {
            socket.current.disconnect()
            socket.current = null
        }

        refreshAfterErrorRef.current = false

        socketLog('io() connecting…', { url: socketUrl, client: 'admin' })

        const nextSocket = io(socketUrl, {
            transports: ['websocket', 'polling'],
            withCredentials: true,
            query: {
                token,
                client: 'admin',
            },
        })

        socket.current = nextSocket
        setSocketInstance(nextSocket)

        nextSocket.on('connect', () => {
            refreshAfterErrorRef.current = false
            const transport = nextSocket.io?.engine?.transport?.name
            socketLog('connected', {
                id: nextSocket.id,
                transport: transport || '(unknown)',
            })
            void dispatch(fetchInAppUnreadCount())
            nextSocket.emit('SOCKET_INFO', {
                id: nextSocket.id,
                ...(profileId ? { profile: profileId } : {}),
            })
        })

        nextSocket.on('disconnect', (reason) => {
            socketLog('disconnected', { reason })
        })

        nextSocket.on('connect_error', (err) => {
            socketLog('connect_error', {
                message: err?.message,
                description: err?.description,
                data: err?.data,
            })
            if (refreshAfterErrorRef.current) return
            refreshAfterErrorRef.current = true
            socketLog('dispatch getSession() after connect_error')
            void dispatch(getSession())
                .unwrap()
                .then(() => socketLog('getSession fulfilled, effect will reconnect'))
                .catch((e) =>
                    socketLog('getSession rejected', e?.message || String(e)),
                )
        })

        if (SOCKET_DEBUG && nextSocket.io) {
            nextSocket.io.on('reconnect_attempt', (n) =>
                socketLog('reconnect_attempt', n),
            )
            nextSocket.io.on('error', (e) => socketLog('manager error', e))
        }

        nextSocket.on('message', handleOnMessage)
        nextSocket.on(IN_APP_NOTIFICATION, handleInAppNotification)

        return () => {
            socketLog('cleanup disconnect')
            nextSocket.off('message', handleOnMessage)
            nextSocket.off(IN_APP_NOTIFICATION, handleInAppNotification)
            nextSocket.disconnect()
            if (socket.current === nextSocket) {
                socket.current = null
            }
            setSocketInstance(null)
        }
    }, [socketUrl, accessToken, dispatch, profileId])

    return (
        <SocketContext.Provider value={socketInstance}>
            {children}
        </SocketContext.Provider>
    )
}

export const SocketProvider = ({ children }) => {
    const { pathname } = useLocation()
    const isAuthPage = AUTH_PAGE_PATHS.includes(pathname)

    if (isAuthPage) {
        return (
            <SocketContext.Provider value={null}>
                {children}
            </SocketContext.Provider>
        )
    }

    return <ConnectedSocketProvider>{children}</ConnectedSocketProvider>
}

export const useSocket = () => useContext(SocketContext)
